import {
	type FormRule,
	FormRulesEnvelopeSchema,
} from "@/features/form-editor/types/form-rule-types";
import type {
	FormDefinition,
	FormDefinitionQueryData,
} from "./form-definition-core.types";
import { extractRawFormRulesFromLayoutJsonWire } from "./layout-json-envelope.schema";

export type FormDefinitionFormRulesInput = Pick<
	FormDefinition,
	"formRules" | "layoutJson"
>;

function parseTopLevelFormRules(raw: unknown): FormRule[] | null {
	if (!Array.isArray(raw)) return null;
	const parsed = FormRulesEnvelopeSchema.safeParse(raw);
	if (!parsed.success) return null;
	return parsed.data;
}

function parseLegacyFormRules(raw: unknown): FormRule[] {
	if (!Array.isArray(raw)) return [];
	const parsed = FormRulesEnvelopeSchema.safeParse(raw);
	if (!parsed.success) return [];
	return parsed.data;
}

function resolveLegacyFromWire(raw: string | null | undefined): FormRule[] {
	const rawRules = extractRawFormRulesFromLayoutJsonWire(raw);
	if (rawRules === undefined) return [];
	return parseLegacyFormRules(rawRules);
}

function isQueryShapedRow(
	api: FormDefinitionFormRulesInput | FormDefinitionQueryData,
): api is FormDefinitionQueryData {
	return "layout" in api;
}

/**
 * Resolves form rules from a form-definition API row or query-selected row.
 * Prefers `formRules` embedded in the `layoutJson` wire string (API persistence
 * location); falls back to top-level `formRules` when the envelope has none.
 */
export function resolveFormRulesFromFormDefinition(
	api: FormDefinitionFormRulesInput | FormDefinitionQueryData,
): FormRule[] {
	const topLevel = parseTopLevelFormRules(api.formRules);

	if (isQueryShapedRow(api)) {
		const legacy = resolveLegacyFromWire(api.wireLayoutJson);
		if (legacy.length > 0) return legacy;
		if (api.formRules.length > 0) return api.formRules;
		if (topLevel !== null) return topLevel;
		return [];
	}

	const rawLayoutJson = api.layoutJson;
	if (typeof rawLayoutJson === "string") {
		const legacy = resolveLegacyFromWire(rawLayoutJson);
		if (legacy.length > 0) return legacy;
	}

	if (topLevel !== null) {
		return topLevel;
	}

	return [];
}
